import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationStart, ParamMap, Router } from '@angular/router';
import { UpdatePaymentGatewayConfig } from '@app/finxone-web-frontend/app/lib/actions/account.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { PaymentGatewayConfig } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { isAuthenticatedRoute } from '@app/finxone-web-frontend/app/lib/utils/auth-checks.utils';
import { logBootTrace } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  APP_ZONES,
  DocumentMonthsUpdate,
  SupportedDevice,
  TransitionPresetType,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';

import { SetFormActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { IdleTimeoutService } from '@app/finxone-web-frontend/app/lib/services/idle-timeout-service/idle-timeout.service';
import { calculateAndSetDateRange } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/generic-actions/download-document.utils';
import { FormID } from '@finxone-platform/form-action';
import { finalize, map, Observable, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { DeviceTypeService } from '../../../lib/services/device-type/device-type.service';
import { RoleState, RoleStateModel } from '../../../lib/state/role.state';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-parent-container',
  templateUrl: './parent-container.component.html',
  styleUrls: ['./parent-container.component.scss'],
  styles: [
    `
      .page-container {
        position: relative;
        width: 100%;
        /* Important: Container needs a defined height */
        height: 100vh; /* or any fixed height that makes sense for your layout */
      }

      /* Style that should be applied to your page components */
      ::ng-deep .page-container > * {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class ParentContainerComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  @Select(RoleState.getRole)
  roles!: Observable<RoleStateModel>;
  roleInfo: RoleStateModel;
  @Input() device: SupportedDevice | null;
  @Input() paymentProviderConfig: PaymentGatewayConfig;
  zoneUrlName = '';
  pageUrlName = '';
  public menuTypeForDesktop = '';
  bodyScrollingOn = true;
  @Input() isModal = false;
  public navDirection: keyof TransitionPresetType = 'forward';

  constructor(
    private readonly deviceTypeService: DeviceTypeService,
    private readonly route: ActivatedRoute,
    private readonly store: Store,
    private readonly router: Router,
    private readonly configService: ConfigService,
    private readonly idleTimeoutService: IdleTimeoutService,
  ) {
    this.idleTimeoutService.setIdleTimeout();
    this.route.paramMap
      .pipe(
        switchMap((params: ParamMap) => {
          const zoneUrlName = params.get('zone');
          let pageUrlName = params.get('page');
          const modalPageUrlName = params.get('modal-page');
          pageUrlName = this.checkForModalAndGetCorrectPageUrl(modalPageUrlName, pageUrlName);
          if (zoneUrlName === null && pageUrlName === null) {
            throw new Error('No zone or page specified via path or input');
          }
          if (zoneUrlName != null && pageUrlName === null) {
            return configService.getZoneWithNoRoleContext().pipe(
              tap((zone) => {
                // for now just default to first page of the zone
                router.navigateByUrl(
                  `/zones/${zoneUrlName}/${
                    zone?.roles['signed-out']['default'][deviceTypeService.currentdeviceType].pages[0].url
                  }`,
                );
              }),
              map(() => null),
            );
          } else {
            this.zoneUrlName = zoneUrlName ?? '';
            this.pageUrlName = this.configService.filterOutOnlyPageUrlFromParam(pageUrlName) ?? '';

            // keep track of current pages forms and register to the workflowFormsService for usage in the workflow form guard
            return this.configService.getCurrentRoleZoneWidgetsConfig().pipe(
              take(1),
              tap((pageConfig) => {
                this.bodyScrollingOn = pageConfig.pageConfiguration?.isBodyScrollingEnabled ?? false;
              }),
              map(() => null),
              // drop the index.html boot spinner once the page config has arrived.
              // finalize (not tap) because getZoneByRoute can complete without
              // emitting when role/zone config isn't ready yet - the spinner must
              // never outlive this chain however it ends
              finalize(() => {
                if (document.getElementById('loader')) {
                  document.getElementById('loader')?.remove();
                  logBootTrace();
                }
              }),
            );
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({ error: () => document.getElementById('loader')?.remove() });
    this.configService.defaultToSignedOutIfNeeded();
    this.setupRouterEventsSubscription();
  }

  ngOnInit() {
    this.roles.subscribe((roles) => {
      this.roleInfo = roles;
      // if we've ended up in the signed out role
      // on an authenticated route reload the page
      if (
        this.roleInfo?.role &&
        this.roleInfo?.role === 'signed-out' &&
        isAuthenticatedRoute(window.location.pathname) &&
        !this.configService.getActiveConfigType().isWorkflow
      ) {
        window.location.reload();
      }
    });
    if (this.paymentProviderConfig?.payment) {
      this.store.dispatch(new UpdatePaymentGatewayConfig(this.paymentProviderConfig));
    }
    if (!this.device) {
      this.device = this.deviceTypeService.deviceType;
    }

    this.setStatementDateRanges();
  }

  private setStatementDateRanges() {
    // Initialize default date range options for statement download functionality
    calculateAndSetDateRange(DocumentMonthsUpdate.LAST_MONTH, this.store);
    calculateAndSetDateRange(DocumentMonthsUpdate.PREVIOUS_3_MONTHS, this.store);
    calculateAndSetDateRange(DocumentMonthsUpdate.PREVIOUS_6_MONTHS, this.store);
  }

  private setupRouterEventsSubscription() {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        const navStartEvent = event as NavigationStart;
        this.setNavigationDirection(navStartEvent);
        this.handleScrollOnNavigation();
      }
    });
  }

  private checkForModalAndGetCorrectPageUrl(
    modalPageUrlName: string | null,
    pageUrlName: string | null,
  ): string | null {
    if (modalPageUrlName) {
      pageUrlName = modalPageUrlName;
      this.menuTypeForDesktop = 'modal';
      this.isModal = true;
    }
    return pageUrlName;
  }

  /**
   * Subscribes to router navigation events and scrolls the window
   * to the top whenever a navigation starts. This ensures that
   * each new page loads from the top, rather than retaining
   * the scroll position from the previous page.
   */
  private handleScrollOnNavigation(): void {
    window.scrollTo(0, 0);
  }

  private setNavigationDirection(navStartEvent: NavigationStart) {
    if (navStartEvent.navigationTrigger === 'popstate' && navStartEvent.restoredState) {
      if (navStartEvent.restoredState.navigationId < navStartEvent.id) {
        this.navDirection = 'backward';
      } else {
        this.navDirection = 'forward';
      }
    } else {
      this.navDirection = 'forward';
    }
    this.setNavigationDirectionFromUrlParam(navStartEvent);
  }

  private setNavigationDirectionFromUrlParam(navStartEvent: NavigationStart) {
    const [, queryString] = navStartEvent.url.split('?');
    if (queryString) {
      const params = new URLSearchParams(queryString);
      const directionParam = params.get('direction');
      if (directionParam === 'forward' || directionParam === 'backward' || directionParam === 'none') {
        this.navDirection = directionParam;
      }
    }
  }
}
